<template>
    <div class="migrol-form__address">
        <div class="migrol-form__address-row">
            <RadiobuttonList :label="Strings['Global.Address.Salutation']"
                             :options="salutationOptions"
                             :value="address.salutation"
                             @change="newVal => updateField('salutation', newVal)"
                             rules="required"
                             :disabled="disableAddressPart" />
        </div>

        <div class="migrol-form__address-row" v-if="!isCompany">
            <InputWithLabel :label="Strings['Global.Address.FirstName']"
                            :value="address.firstName"
                            @change="newVal => updateField('firstName', newVal)"
                            rules="required"
                            name="fname"
                            autocomplete="given-name"
                            :disabled="disableAddressPart" />
        </div>
        <div class="migrol-form__address-row" v-if="!isCompany">
            <InputWithLabel :label="Strings['Global.Address.LastName']"
                            :value="address.lastName"
                            @change="newVal => updateField('lastName', newVal)"
                            rules="required"
                            name="lname"
                            autocomplete="family-name"
                            :disabled="disableAddressPart" />
        </div>
        <div class="migrol-form__address-row" v-if="isCompany">
            <InputWithLabel :label="Strings['Global.Address.CompanyName']"
                            :value="address.companyName"
                            @change="newVal => updateField('companyName', newVal)"
                            rules="required"
                            name="organization"
                            autocomplete="organization"
                            :disabled="disableAddressPart" />
        </div>
        <div class="migrol-form__address-row" v-if="showStreetAndNr">
            <StreetAutocomplete :label="Strings['Global.Address.Street']"
                                :value="address.street"
                                @change="newVal => updateStreet(newVal)"
                                flex="4"
                                :rules="streetRules"
                                name="address"
                                autocomplete="address-line1"
                                :zipcode="zipcodeForStreetAutocomplete"
                                :disabled="disableAddressPart" />
            <InputWithLabel :label="Strings['Global.Address.HouseNr']"
                            :value="address.streetNumber"
                            maxlength="10"
                            @change="newVal => updateField('streetNumber', newVal)"
                            flex="1"
                            :disabled="disableAddressPart" />
        </div>
        <div class="migrol-form__address-row" v-if="showStreetAndNr">
            <a href="#" v-if="!addressAdditionVisible" @click.prevent="expandAddressAddition">{{Strings['Global.Address.ExpandAddress']}}</a>
            <InputWithLabel :label="Strings['Global.Address.AddressAddition']"
                            :value="address.addressLine2"
                            autocomplete="address-line2"
                            @change="newVal => updateField('addressLine2', newVal)"
                            :disabled="disableAddressPart"
                            v-if="addressAdditionVisible" />
        </div>
        <div class="migrol-form__address-row" v-if="showZipCity">
            <ZipCityWithLabel :label="Strings['Global.Address.ZipCity']"
                              :zipCity="{ zipcode: address.zipcode, city: address.city, countryCode: address.countryCode }"
                              @change="newVal => updateZipCity(newVal)"
                              v-if="allowZipCityEdit && !disableAddressPart"
                              :rules="zipCityRules" />
            <InputWithLabel :label="Strings['Global.Address.ZipCity']"
                            :value="address.zipcode + ' ' + address.city"
                            :disabled="true"
                            v-if="!allowZipCityEdit || disableAddressPart" />
        </div>
        <div class="migrol-form__address-row" v-if="showFixAddressButton">
            <div class="migrol-form__address-edit">
                <button class="migrol-form__address-edit-button" @click="fixAddressClicked"><span class="migrol-form__address-edit-icon"><i class="fas fa-pencil-alt"></i></span> {{Strings['Global.Address.ButtonAdjustAddress']}}</button>
                <InfoIcon position="right"><span v-html="Strings['Global.Address.AdjustAddressWarning']"></span></InfoIcon>
            </div>
        </div>
        <div class="migrol-form__address-row" v-if="showPhone">
            <InputWithLabel :label="Strings['Global.Address.Phone']"
                            :value="address.phone"
                            name="phone"
                            autocomplete="tel"
                            @change="newVal => updateField('phone', newVal)"
                            :rules="phoneRules"
                            type="tel" />
        </div>
        <div class="migrol-form__address-row" v-if="showEmail">
            <InputWithLabel :label="Strings['Global.Address.Email']"
                            :value="address.emailAddress"
                            @change="newVal => updateField('emailAddress', newVal)"
                            rules="required"
                            name="email"
                            autocomplete="email"
                            type="email" />
        </div>
        <div class="migrol-form__address-row" v-if="cumulusVisible">
            <InputWithLabel :label="Strings['Global.Address.CumulusNumber']"
                            :value="cumulusNumberWithoutPrefix"
                            @change="newVal => updateField('cumulusNumber', '2099' + newVal)"
                            type="number"
                            name="cumulus"
                            prefix="2099"
                            rules="cumulus" />
        </div>
        <div class="migrol-form__address-row" v-if="verifyAddress">
            <ValidationProvider name="verifyAddressVal" :rules="addressVerificationRule" v-slot="{ errors }" mode="passive" :vid="$id('verifyAddressVid')" slim ref="addressVerificationValidatorProvider">
                <div class="migrol-form__address-check-wrapper">
                    <input :id="$id('verifyAddressInput')" type="hidden" v-model="addressVerifyObject">
                    <div class="migrol-form__error-message-block" v-if="errors.length > 0 && !addressCheckLoading">
                        <div class="migrol-form__error-message">{{Strings['Global.Address.CheckFailed']}}</div>
                        <div class="migrol-form__buttons">
                            <button class="migrol-form__button" @click="doAddressVerification">{{Strings['Global.Address.CheckAgain']}}</button>
                            <button class="migrol-form__button migrol-form__button--green" @click="overruleAddressVerification">{{Strings['Global.Address.CheckOverrule']}}</button>
                        </div>
                    </div>
                    <div v-if="errors.length > 0 && addressCheckLoading">
                        <i class="fas fa-spinner fa-spin" style="font-size: 2.5rem"></i>
                    </div>
                </div>
            </ValidationProvider>
        </div>
    </div>
</template>

<script>
    import { Salutations } from '@/utils/constants';
    import InputWithLabel from './InputWithLabel';
    import RadiobuttonList from './RadiobuttonList';
    import ZipCityWithLabel from './ZipCityWithLabel';
    import InfoIcon from './InfoIcon';
    import { isNullOrUndefined } from 'util';
    import { ValidationProvider } from 'VeeValidate';

    export default {
        props: {
            showStreetAndNr: {
                type: Boolean,
                default: true
            },
            showZipCity: {
                type: Boolean,
                default: true
            },
            showPhone: {
                type: Boolean,
                default: true
            },
            showEmail: {
                type: Boolean,
                default: true
            },
            showCumulus: {
                type: Boolean,
                default: true
            },
            allowZipCityEdit: {
                type: Boolean,
                default: true
            },
            disableAddressPart: {
                type: Boolean,
                default: false
            },
            showFixAddressButton: {
                type: Boolean,
                default: false
            },
            address: {
                type: Object,
                required: true
            },
            streetRules: {
                type: String,
                required: false,
                default: 'required'
            },
            zipCityRules: {
                type: String,
                required: false,
                default: 'required'
            },
            phoneOptional: {
                type: Boolean,
                required: false,
                default: false
            }
        },
        data() {
            return {
                salutations: Salutations,
                streetForceUpdateKey: 1,
                addressAdditionExpanded: false,
                addressVerificationOverruled: false,
                addressCheckLoading: false
            }
        },
        computed: {
            isCompany() {
                return this.address.salutation === Salutations.Company;
            },
            Strings() {
                return this.$migrolVueLibraryData.Strings;
            },
            salutationOptions() {
                return [
                    { text: this.Strings['Enums.Salutation.Mrs'], value: Salutations.Mrs },
                    { text: this.Strings['Enums.Salutation.Mr'], value: Salutations.Mr },
                    { text: this.Strings['Enums.Salutation.Company'], value: Salutations.Company }
                ];
            },
            cumulusNumberWithoutPrefix() {
                if (this.address.cumulusNumber && this.address.cumulusNumber.indexOf('2099') === 0)
                    return this.address.cumulusNumber.substring(4);
                else
                    return '';
            },
            zipcodeForStreetAutocomplete() {
                var zip = '';
                if (!this.allowZipCityEdit && !this.disableAddressPart && this.address.zipcode != null && this.address.zipcode > 0) {
                    zip = String(this.address.zipcode);
                }
                return zip;
            },
            cumulusVisible() {
                //showCumulus is to enable cumulus in general, but even if enabled, the field is only shown for non-companies
                return this.showCumulus && (this.address.salutation != Salutations.Company);
            },
            addressAdditionVisible() {
                //let hasValue = this.address.addressLine2 && this.address.addressLine2 != '';
                return this.address.addressLine2 || this.addressAdditionExpanded || this.disableAddressPart;
            },
            verifyAddress() {
                return !this.addressVerificationOverruled
                    && !this.disableAddressPart
                    && this.showStreetAndNr //only verify, if street/nr are actually shown
                    && this.address.street
                    && this.showZipCity //only verify if zip/city are actually shown
                    && this.address.zipcode
                    && this.address.city
                    ;
            },
            addressVerificationRule() {
                if (this.verifyAddress)
                    return 'address_valid';
                else
                    return '';
            },
            addressVerifyObject() {
                return { street: this.address.street, houseNr: this.address.streetNumber, zipcode: this.address.zipcode, city: this.address.city };
            },
            phoneRules() {
                return this.phoneOptional ? 'phone' : 'required|phone';
            }
        },
        methods: {
            fixAddressClicked() {
                this.$emit('fixAddress');
            },
            updateField(propertyName, newValue) {
                var updatedAddress = this.address;

                if (propertyName === 'cumulusNumber' && newValue === '2099')
                    newValue = null;

                if (propertyName === 'salutation') {
                    if (newValue === Salutations.Company) {
                        updatedAddress['firstName'] = null;
                        updatedAddress['lastName'] = null;
                    } else {
                        updatedAddress['companyName'] = null;
                    }
                }

                if (newValue && (typeof newValue === 'string' || newValue instanceof String))
                    updatedAddress[propertyName] = newValue.trim(); //if it's a string, we trim it
                else
                    updatedAddress[propertyName] = newValue;

                this.$emit('change', updatedAddress);
            },
            updateStreet(newValue) {
                let vm = this;
                var updatedAddress = vm.address;
                let match = newValue.match(/^(.+)(\s+\d+\s?[A-z]?)$/);
                if (match && match.length === 3 && (isNullOrUndefined(vm.address.streetNumber) || vm.address.streetNumber.trim() === '')) {
                    updatedAddress.street = match[1].trim();
                    updatedAddress.streetNumber = match[2].trim();
                } else {
                    updatedAddress.street = newValue.trim();
                }
                this.$emit('change', updatedAddress);
            },
            updateZipCity(newValue) {
                var updatedAddress = this.address;
                updatedAddress.zipcode = newValue.zipcode;
                updatedAddress.city = newValue.city;
                updatedAddress.countryCode = newValue.countryCode;
                this.$emit('change', updatedAddress);
            },
            expandAddressAddition() {
                this.addressAdditionExpanded = true;
            },
            overruleAddressVerification() {
                this.addressVerificationOverruled = true;
            },
            doAddressVerification() {
                var vm = this;
                vm.addressCheckLoading = true;
                setTimeout(function () { vm.addressCheckLoading = false; }, 300);
                vm.$refs['addressVerificationValidatorProvider'].validate();
            },
        },
        components: {
            InputWithLabel,
            ZipCityWithLabel,
            RadiobuttonList,
            InfoIcon,
            ValidationProvider
        }
    };
</script>